import type { TerminalAuth } from "@cloudserver/contracts";
import {
  kindOf,
  RESTORED,
  type Layout,
  type SessionKind,
  type TerminalState,
  type TerminalTab,
} from "./store.js";

/*
 * The open sessions of this browser tab, kept across a page reload
 * (docs/terminal.md, "After a reload"; owner's report of 2026-10-22: a
 * reload made every session tab vanish). Only who each tab is, and a
 * terminal's one-time resume token — never a password, ticket, certificate
 * or anything typed — in `sessionStorage`,
 * which belongs to this one browser tab and ends with it: a reload rebuilds
 * the tabs in the same order and reconnects them (`restore.ts`), a tab
 * closed with ✕ is gone from here at once.
 */

/** Where the descriptors live (per browser tab). */
export const SESSIONS_KEY = "cs.sessions.open";
/** The shape's version: anything else is dropped as unreadable. */
const VERSION = 1;
/** More than the API's per-person limits ever lets open; a bigger list is damaged. */
const MAX_TABS = 32;

/** Who a tab is: enough to open it again, nothing secret. */
export interface SessionDescriptor {
  id: string;
  kind: SessionKind;
  projectId: string;
  serverId: string;
  serverName: string;
  osFlavor: string | null;
  osVersion: string | null;
  /** The Unix user, the Windows user of a desktop, empty for a console. */
  unixUser: string;
  /** How a terminal signs in; a password one asks for the password again. */
  auth: TerminalAuth;
  /**
   * A terminal's one-time token back to its kept shell (bound on the API to
   * this person, browser session and server; used once).
   */
  resumeToken?: string;
}

/** The tabs in bar order, which one had the focus, and the split view. */
export interface SavedSessions {
  v: typeof VERSION;
  /** The person they belong to: another one signing in to this tab gets none of them. */
  userId: string;
  tabs: SessionDescriptor[];
  active: string | null;
  layout: Layout;
  panes: string[];
}

export function describeTab(tab: TerminalTab): SessionDescriptor {
  return {
    id: tab.id,
    kind: kindOf(tab),
    projectId: tab.projectId,
    serverId: tab.serverId,
    serverName: tab.serverName,
    osFlavor: tab.osFlavor,
    osVersion: tab.osVersion,
    unixUser: tab.unixUser,
    auth: tab.auth,
    ...(kindOf(tab) === "ssh" && tab.resumeToken ? { resumeToken: tab.resumeToken } : {}),
  };
}

/** What is kept of the store's state now. */
export function describeSessions(
  state: Pick<TerminalState, "tabs" | "active" | "layout" | "panes">,
  userId: string,
): SavedSessions {
  return {
    v: VERSION,
    userId,
    tabs: state.tabs.map(describeTab),
    active: state.active,
    layout: state.layout,
    panes: state.panes,
  };
}

const KINDS: readonly SessionKind[] = ["ssh", "rdp", "vnc"];
const LAYOUTS: readonly Layout[] = ["single", "columns", "rows"];
const AUTHS: readonly TerminalAuth[] = ["certificate", "password"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const text = (value: unknown, max = 256): value is string =>
  typeof value === "string" && value.length <= max;
const textOrNull = (value: unknown): value is string | null => value === null || text(value);
const TOKEN = /^[\w-]{43}$/;

function descriptorOf(value: unknown): SessionDescriptor | null {
  if (!isRecord(value)) return null;
  const { id, kind, projectId, serverId, serverName, osFlavor, osVersion, unixUser, auth } = value;
  const { resumeToken } = value;
  if (
    !text(id) ||
    !id ||
    !KINDS.includes(kind as SessionKind) ||
    !text(projectId) ||
    !projectId ||
    !text(serverId) ||
    !serverId ||
    !text(serverName) ||
    !textOrNull(osFlavor) ||
    !textOrNull(osVersion) ||
    !text(unixUser) ||
    !AUTHS.includes(auth as TerminalAuth)
  )
    return null;
  return {
    id,
    kind: kind as SessionKind,
    projectId,
    serverId,
    serverName,
    osFlavor,
    osVersion,
    unixUser,
    auth: auth as TerminalAuth,
    ...(kind === "ssh" && typeof resumeToken === "string" && TOKEN.test(resumeToken)
      ? { resumeToken }
      : {}),
  };
}

/**
 * The saved sessions of `userId`, or null: nothing saved, damaged, of
 * another version or of another person. Damaged tabs are left out one by
 * one; duplicates keep their first place; `active` and `panes` keep only
 * tabs that are there.
 */
export function parseSaved(raw: string | null, userId: string): SavedSessions | null {
  if (!raw) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(value) || value.v !== VERSION || value.userId !== userId) return null;
  if (!Array.isArray(value.tabs) || value.tabs.length > MAX_TABS) return null;
  const tabs: SessionDescriptor[] = [];
  for (const item of value.tabs) {
    const tab = descriptorOf(item);
    if (tab && !tabs.some((t) => t.id === tab.id)) tabs.push(tab);
  }
  const ids = new Set(tabs.map((tab) => tab.id));
  const active = typeof value.active === "string" && ids.has(value.active) ? value.active : null;
  const layout = LAYOUTS.includes(value.layout as Layout) ? (value.layout as Layout) : "single";
  const panes = Array.isArray(value.panes)
    ? value.panes.filter((p): p is string => typeof p === "string" && ids.has(p)).slice(0, 2)
    : [];
  return { v: VERSION, userId, tabs, active: active ?? tabs[0]?.id ?? null, layout, panes };
}

/**
 * A tab that cannot reconnect by itself: its password is needed (never
 * kept). A password terminal with a resume token tries its kept shell first.
 */
export const needsPassword = (
  tab: Pick<SessionDescriptor, "kind" | "auth" | "resumeToken">,
): boolean =>
  tab.kind === "rdp" || (tab.kind === "ssh" && tab.auth === "password" && !tab.resumeToken);

/**
 * The tab as it comes back: connecting, or waiting for the password. A new
 * session in every way — no session id, host key, unseen output or the
 * Autopilot's access (it starts at «Выключено»).
 */
export function restoredTab(saved: SessionDescriptor): TerminalTab {
  const waiting = needsPassword(saved);
  return {
    id: saved.id,
    kind: saved.kind,
    projectId: saved.projectId,
    serverId: saved.serverId,
    serverName: saved.serverName,
    osFlavor: saved.osFlavor,
    osVersion: saved.osVersion,
    unixUser: saved.unixUser,
    auth: saved.auth,
    status: waiting ? "disconnected" : "connecting",
    failure: null,
    offeredHostKey: null,
    problem: waiting ? RESTORED : null,
    hostKeyFingerprint: null,
    sessionId: null,
    unseen: false,
    resumeToken: saved.resumeToken ?? null,
  };
}

/** Where the descriptors are kept. */
export interface SessionStorageLike {
  read(): string | null;
  write(value: string | null): void;
}

/** `sessionStorage`; refused storage (private windows, quotas) just keeps nothing. */
export const browserSessions: SessionStorageLike = {
  read() {
    try {
      return sessionStorage.getItem(SESSIONS_KEY);
    } catch {
      return null;
    }
  },
  write(value) {
    try {
      if (value === null) sessionStorage.removeItem(SESSIONS_KEY);
      else sessionStorage.setItem(SESSIONS_KEY, value);
    } catch {
      // Nothing kept: a reload starts without tabs, as before.
    }
  },
};

/**
 * Keeps the store's sessions in `storage` from now on: written when who
 * the tabs are, their order, the focus or the split view change (not on
 * every status change), removed when the last tab closes.
 */
export function persistSessions(
  store: {
    subscribe: (listener: () => void) => () => void;
    snapshot: () => Pick<TerminalState, "tabs" | "active" | "layout" | "panes">;
  },
  userId: string,
  storage: SessionStorageLike = browserSessions,
): () => void {
  let last: string | null | undefined;
  const save = () => {
    const state = store.snapshot();
    const next = state.tabs.length === 0 ? null : JSON.stringify(describeSessions(state, userId));
    if (next === last) return;
    last = next;
    storage.write(next);
  };
  save();
  return store.subscribe(save);
}
